// src/components/DocumentDetector.jsx
import React, { useState, useRef, useCallback } from 'react';
import { useOpenCvWorker } from './hooks/useOpenCvWorker';

function DocumentDetector() {
    const [image, setImage] = useState(null);
    const [detectionResult, setDetectionResult] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    
    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);
    const resultCanvasRef = useRef(null);
    
    const { 
        isReady, 
        loading, 
        version,
        loadSource,
        error: workerError,
        detectDocument,
        validateDocument,
        reloadOpenCv
    } = useOpenCvWorker();

    // Загрузка изображения
    const handleFileUpload = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                setImage(img);
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                setDetectionResult(null);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }, []);

    // Детекция документа
    const handleDetectDocument = useCallback(async () => {
        if (!image || !isReady || processing) return;
        
        setProcessing(true);
        setError(null);
        
        try {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            
            const result = await detectDocument(
                imageData.data,
                imageData.width,
                imageData.height
            );
            
            if (result) {
                setDetectionResult(result);
                
                // Отображаем результат
                const resultCanvas = resultCanvasRef.current;
                const resultCtx = resultCanvas.getContext('2d');
                resultCanvas.width = result.warped.width;
                resultCanvas.height = result.warped.height;
                resultCtx.putImageData(result.warped, 0, 0);
            } else {
                setError('Документ не найден на изображении');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setProcessing(false);
        }
    }, [image, isReady, processing, detectDocument]);

    // Сброс
    const handleReset = useCallback(() => {
        if (!image) return;
        
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(image, 0, 0);
        setDetectionResult(null);
        setError(null);
    }, [image]);

    // Перезагрузка OpenCV
    const handleReload = useCallback(async () => {
        try {
            await reloadOpenCv();
            console.log('OpenCV перезагружен');
        } catch (err) {
            console.error('Ошибка перезагрузки:', err);
        }
    }, [reloadOpenCv]);

    if (loading) {
        return (
            <div className="document-detector loading">
                <div className="spinner">⏳</div>
                <p>Загрузка OpenCV с вашего сервера...</p>
                <p className="info">Файл: /opencv/opencv.js</p>
            </div>
        );
    }

    console.log({detectionResult});

    return (
        <div className="document-detector">
            <h2>Детекция документов</h2>
            
            {/* Информация о загрузке */}
            <div className="status-bar">
                <span className="status-badge">
                    {isReady ? '✅ OpenCV готов' : '❌ OpenCV не загружен'}
                </span>
                {version && <span>Версия: {version}</span>}
                {loadSource && <span>Источник: {loadSource}</span>}
                <button onClick={handleReload} className="reload-btn">
                    🔄 Перезагрузить
                </button>
            </div>
            
            <div className="controls">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                />
                <button 
                    onClick={handleDetectDocument} 
                    disabled={!image || processing || !isReady}
                >
                    {processing ? '⏳ Обработка...' : '🔍 Найти документ'}
                </button>
                <button onClick={handleReset} disabled={!image}>
                    🔄 Сброс
                </button>
            </div>
            
            {error && (
                <div className="error-message">
                    ❌ {error}
                </div>
            )}
            
            <div className="canvas-container">
                <div className="canvas-wrapper">
                    <h3>Исходное изображение</h3>
                    <canvas ref={canvasRef} />
                </div>
                
                <div className="canvas-wrapper">
                    <h3>Результат</h3>
                    <canvas ref={resultCanvasRef} />
                    {!detectionResult && !processing && image && (
                        <div className="placeholder">
                            Нажмите "Найти документ"
                        </div>
                    )}
                    {processing && (
                        <div className="processing-overlay">⏳ Обработка...</div>
                    )}
                </div>
            </div>
            
            {detectionResult && (
                <div className="detection-info">
                    <div className="info-row">
                        <span>Уверенность:</span>
                        <span className="confidence">{detectionResult.confidence}%</span>
                    </div>
                    <div className="info-row">
                        <span>Размер документа:</span>
                        <span>{detectionResult.warped?.width} x {detectionResult?.warped?.height}px</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DocumentDetector;